import React, { useState } from 'react';
import { Pill, Check, Clock, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { backendUrl, fmtIST, errMsg } from './recoveryUi';

const DOSE_STYLE = {
  taken: ['Taken', '#047857', '#ecfdf5'], missed: ['Missed', '#b91c1c', '#fef2f2'],
  snoozed: ['Snoozed', '#b45309', '#fffbeb'], cancelled: ['Stopped', '#64748b', '#f1f5f9'],
};

export default function TodayMeds({ planId, doses, rx, onChanged }) {
  const [busy, setBusy] = useState('');
  const now = Date.now();
  const act = async (id, action) => {
    setBusy(id);
    try { await api.post(`/patient/recovery/${planId}/dose/${id}`, { action }); onChanged(); }
    catch (e) { toast.error(errMsg(e, 'Could not update this dose')); }
    finally { setBusy(''); }
  };
  const dueNow = doses.filter((d) => ['pending', 'snoozed'].includes(d.status) && new Date(d.due_at).getTime() <= now);

  return (
    <section className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm" data-testid="recovery-today-meds">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-[15px] font-extrabold text-slate-900 flex items-center gap-2"><Pill size={17} className="text-teal-700" /> Today's medicines</h2>
        {rx && (
          <a href={backendUrl(rx.downloadUrl)} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-teal-700 text-white text-[12px] font-bold" data-testid="recovery-open-rx">
            <FileText size={13} /> Prescription
          </a>
        )}
      </div>
      {dueNow.length > 0 && (
        <div className="mt-3 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-[13px] font-bold" data-testid="recovery-doses-due-now">
          {dueNow.length} dose{dueNow.length > 1 ? 's' : ''} due now
        </div>
      )}
      {doses.length === 0 && <p className="mt-3 text-[13px] text-slate-500" data-testid="recovery-no-doses">No scheduled doses today.</p>}
      <ul className="mt-3 divide-y divide-slate-100">
        {doses.map((d) => {
          const st = DOSE_STYLE[d.status];
          const canTake = ['pending', 'snoozed'].includes(d.status) && new Date(d.due_at).getTime() - 60 * 60000 <= now;
          return (
            <li key={d.id} className="py-2.5 flex items-center gap-3" data-testid={`recovery-dose-${d.id}`}>
              <div className="w-14 text-[12px] font-bold text-slate-600 flex items-center gap-1"><Clock size={12} />{fmtIST(d.due_at, false)}</div>
              <div className="flex-1 min-w-0 text-[14px] font-semibold text-slate-900 truncate">{d.med_name}{d.late && <span className="ml-1 text-[11px] text-amber-700">(late)</span>}</div>
              {st && d.status !== 'snoozed' ? (
                <span className="px-2 py-0.5 rounded-full text-[11px] font-bold" style={{ color: st[1], background: st[2] }}>{st[0]}</span>
              ) : canTake ? (
                <div className="flex gap-1.5">
                  <button type="button" disabled={busy === d.id} onClick={() => act(d.id, 'taken')}
                          className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-[12px] font-bold flex items-center gap-1" data-testid={`recovery-dose-taken-${d.id}`}><Check size={13} /> Taken</button>
                  <button type="button" disabled={busy === d.id} onClick={() => act(d.id, 'snooze')}
                          className="px-2.5 py-1.5 rounded-lg border border-slate-300 text-[12px] font-semibold" data-testid={`recovery-dose-snooze-${d.id}`}>15 min</button>
                </div>
              ) : <span className="text-[11px] text-slate-400">{d.status === 'snoozed' ? 'Snoozed' : 'Upcoming'}</span>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
